/**
 * entityIdProcessor.js - Unified Entity ID Processing System
 * 
 * SINGLE SOURCE OF TRUTH for all entity ID handling in the application.
 * This module provides consistent rules for:
 *   - Linking entity IDs (making them clickable)
 *   - Stripping entity IDs (removing from display)
 *   - Suppressing specific entity IDs (context-aware removal)
 *   - Normalizing redundant prefixes from LLM responses
 *   - Cleaning markdown formatting around entity IDs
 * 
 * USAGE:
 *   import { processEntityIds, hasEntityIds, ENTITY_CONFIG } from './utils/entityIdProcessor';
 *   
 *   // Get processed text with entity info
 *   const result = processEntityIds(text, { mode: 'link', suppressEntityId: currentId });
 *   
 * @module entityIdProcessor
 */

// =============================================================================
// ENTITY CONFIGURATION - Single Source of Truth
// =============================================================================

/**
 * LINKABLE ENTITIES
 * These entities have edit modals and can be clicked to open them.
 * 
 * Rule: If an entity has a detail/edit modal, it is linkable.
 * When linkable=true, IDs are rendered as clickable links.
 */
export const LINKABLE_ENTITIES = {
  // Feature - has FeatureDetail modal
  feat: { type: 'feature', color: 'text-blue-600 dark:text-blue-400', label: 'Feature' },
  feature: { type: 'feature', color: 'text-blue-600 dark:text-blue-400', label: 'Feature' },
  
  // Epic - has EpicDetailModal
  epic: { type: 'epic', color: 'text-purple-600 dark:text-purple-400', label: 'Epic' },
  
  // Goal - has GoalDetailModal
  goal: { type: 'goal', color: 'text-green-600 dark:text-green-400', label: 'Goal' },
  
  // Initiative - has InitiativeDetailModal
  init: { type: 'initiative', color: 'text-amber-600 dark:text-amber-400', label: 'Initiative' },
  initiative: { type: 'initiative', color: 'text-amber-600 dark:text-amber-400', label: 'Initiative' },
  
  // Persona - has PersonaDetailModal
  persona: { type: 'persona', color: 'text-pink-600 dark:text-pink-400', label: 'Persona' },
  pers: { type: 'persona', color: 'text-pink-600 dark:text-pink-400', label: 'Persona' },
  
  // Release - has ReleaseDetailModal
  release: { type: 'release', color: 'text-orange-600 dark:text-orange-400', label: 'Release' },
  rel: { type: 'release', color: 'text-orange-600 dark:text-orange-400', label: 'Release' },
  
  // Request - has RequestDetailModal
  request: { type: 'request', color: 'text-cyan-600 dark:text-cyan-400', label: 'Request' },
  req: { type: 'request', color: 'text-cyan-600 dark:text-cyan-400', label: 'Request' },
  
  // Report - has report viewer
  rpt: { type: 'report', color: 'text-indigo-600 dark:text-indigo-400', label: 'Report' },
  report: { type: 'report', color: 'text-indigo-600 dark:text-indigo-400', label: 'Report' },
  
  // Market Segment - has SegmentDetailModal
  seg: { type: 'segment', color: 'text-teal-600 dark:text-teal-400', label: 'Segment' },
  segment: { type: 'segment', color: 'text-teal-600 dark:text-teal-400', label: 'Segment' },
};

/**
 * COMPOSITE KPI KEY CONFIGURATION
 * KPIs use composite keys in format: goal_xxx:kpi_xxx
 * These are linkable via the parent Goal modal (KPI tab)
 */
export const COMPOSITE_KPI_CONFIG = {
  type: 'kpi_composite',
  color: 'text-indigo-600 dark:text-indigo-400',
  label: 'KPI',
  // Opens Goal modal with KPI tab
  targetType: 'goal',
  targetTab: 'kpis',
};

/**
 * Pattern to match composite KPI keys: goal_xxx:kpi_xxx or goal_xxx:xxx
 * Format: goal_[hex]+:[identifier]
 */
export const COMPOSITE_KPI_PATTERN = /goal_[a-z0-9]+:(?:kpi_)?[a-z0-9_]+/gi;

/**
 * Parse a composite KPI key into its components
 * @param {string} compositeKey - Format: "goal_xxx:kpi_xxx" or "goal_xxx:xxx"
 * @returns {{ goalId: string, kpiId: string } | null}
 */
export const parseCompositeKpiKey = (compositeKey) => {
  if (!compositeKey || typeof compositeKey !== 'string') return null;
  
  const colonIndex = compositeKey.indexOf(':');
  if (colonIndex === -1) return null;
  
  const goalId = compositeKey.substring(0, colonIndex);
  const kpiPart = compositeKey.substring(colonIndex + 1);
  
  // Validate goal_id format
  if (!goalId.startsWith('goal_')) return null;
  
  // kpiPart might be "kpi_xxx" or just "xxx"
  const kpiId = kpiPart.startsWith('kpi_') ? kpiPart : `kpi_${kpiPart}`;
  
  return { goalId, kpiId, fullKey: compositeKey };
};

/**
 * Check if a string is a composite KPI key
 * @param {string} str - String to check
 * @returns {boolean}
 */
export const isCompositeKpiKey = (str) => {
  if (!str || typeof str !== 'string') return false;
  return /^goal_[a-z0-9]+:(?:kpi_)?[a-z0-9_]+$/i.test(str);
};

/**
 * STRIP-ONLY ENTITIES
 * These entities do NOT have edit modals or linking is not meaningful.
 * Their IDs are stripped (removed) from display text.
 * 
 * Rule: If showing the ID provides no actionable value, strip it.
 */
export const STRIP_ENTITIES = {
  // Product - no standalone modal (context is always known)
  prod: { type: 'product', reason: 'Product context is implicit, no modal to link to' },
  product: { type: 'product', reason: 'Product context is implicit, no modal to link to' },
  
  // Competitor - embedded entity, name provides context
  comp: { type: 'competitor', reason: 'Embedded entity, name provides sufficient context' },
  competitor: { type: 'competitor', reason: 'Embedded entity, name provides sufficient context' },
  
  // KPI - simple kpi_xxx format is stripped (no standalone modal)
  // NOTE: Composite keys (goal_xxx:kpi_xxx) are handled separately as linkable
  kpi: { type: 'kpi', reason: 'Simple KPI IDs stripped; composite keys are linkable via goal' },
  
  // Organization - system entity, not user-facing
  org: { type: 'organization', reason: 'System entity, not user-actionable' },
  
  // User - no user profile modal in current UI
  user: { type: 'user', reason: 'No user profile modal available' },
};

/**
 * Combined entity configuration for easy lookup
 */
export const ENTITY_CONFIG = {
  linkable: LINKABLE_ENTITIES,
  strip: STRIP_ENTITIES,
};

/**
 * Get all entity prefixes (both linkable and strip)
 */
const ALL_PREFIXES = [
  ...Object.keys(LINKABLE_ENTITIES),
  ...Object.keys(STRIP_ENTITIES),
];

// =============================================================================
// REGEX PATTERNS
// =============================================================================

/**
 * Base pattern for entity ID format: prefix_hexstring
 * Matches: feat_abc123, epic_def456, kpi_abc_def (kpi can have underscores)
 */
const ENTITY_ID_BASE = `(${ALL_PREFIXES.join('|')})_[a-f0-9_]+`;

/**
 * Pattern to match entity IDs with optional markdown formatting
 * 
 * Captures:
 *   Group 1: Opening formatting (**, `, (, or combinations)
 *   Group 2: The entity prefix (feat, epic, goal, etc.)
 *   Group 3: Full entity ID (prefix_hexstring)
 *   Group 4: Closing formatting
 * 
 * Formats handled:
 *   - feat_xxx (bare)
 *   - (feat_xxx) (parenthesized)
 *   - `feat_xxx` (backticked)
 *   - **feat_xxx** (bold)
 *   - **`feat_xxx`** (bold + backtick)
 *   - `**feat_xxx**` (backtick + bold)
 */
const FORMATTED_ENTITY_PATTERN = new RegExp(
  // Opening: optional markdown formatting
  `(\\*\\*\`?|\`\\*\\*|\`|\\()?` +
  // Entity ID with captured prefix
  `((${ALL_PREFIXES.join('|')})_[a-f0-9_]+)` +
  // Closing: optional markdown formatting  
  `(\`?\\*\\*|\\*\\*\`|\`|\\))?`,
  'gi'
);

/**
 * Pattern for stripping IDs - includes optional horizontal whitespace and parentheses
 * Used when completely removing an ID from text
 * 
 * IMPORTANT: Uses [ \t]* (spaces/tabs only) instead of \s* to preserve newlines.
 * This prevents collapsing blank lines that separate markdown headers.
 * 
 * Matches with surrounding context to clean up spacing:
 *   - " prod_xxx " -> " "
 *   - "(prod_xxx)" -> ""
 *   - " (prod_xxx) " -> " "
 */
const createStripPattern = (prefix) => {
  return new RegExp(`[ \\t]*\\(?${prefix}_[a-f0-9_]+\\)?[ \\t]*`, 'gi');
};

/**
 * Pattern for redundant prefix normalization
 * LLMs sometimes produce: product_prod_xxx, feature_feat_xxx, etc.
 * 
 * These patterns normalize to the canonical short form.
 */
const REDUNDANT_PREFIX_PATTERNS = [
  { pattern: /^product_prod_/i, replacement: 'prod_' },
  { pattern: /^feature_feat_/i, replacement: 'feat_' },
  { pattern: /^request_request_/i, replacement: 'request_' },
  { pattern: /^epic_epic_/i, replacement: 'epic_' },
  { pattern: /^goal_goal_/i, replacement: 'goal_' },
  { pattern: /^initiative_init_/i, replacement: 'init_' },
  { pattern: /^persona_pers_/i, replacement: 'pers_' },
  { pattern: /^release_rel_/i, replacement: 'rel_' },
];

// =============================================================================
// CORE PROCESSING FUNCTIONS
// =============================================================================

/**
 * Normalize an entity ID by fixing redundant prefixes
 * 
 * LLMs sometimes produce IDs like "product_prod_abc123" which should be "prod_abc123"
 * This function normalizes these to the canonical form.
 * 
 * @param {string} entityId - The entity ID to normalize
 * @returns {string} The normalized entity ID
 * 
 * @example
 * normalizeEntityId('product_prod_abc123') // returns 'prod_abc123'
 * normalizeEntityId('feat_abc123') // returns 'feat_abc123' (unchanged)
 */
export const normalizeEntityId = (entityId) => {
  if (!entityId || typeof entityId !== 'string') return entityId;
  
  for (const { pattern, replacement } of REDUNDANT_PREFIX_PATTERNS) {
    if (pattern.test(entityId)) {
      return entityId.replace(pattern, replacement);
    }
  }
  return entityId;
};

/**
 * Extract the prefix from an entity ID
 * 
 * @param {string} entityId - The entity ID (e.g., 'feat_abc123')
 * @returns {string|null} The prefix (e.g., 'feat') or null if not valid
 */
export const extractEntityPrefix = (entityId) => {
  if (!entityId || typeof entityId !== 'string') return null;
  
  const normalized = normalizeEntityId(entityId);
  const match = normalized.match(/^([a-z]+)_/i);
  return match ? match[1].toLowerCase() : null;
};

/**
 * Check if an entity ID is linkable (has an edit modal)
 * 
 * @param {string} entityId - The entity ID to check
 * @returns {boolean} True if the entity can be linked
 */
export const isLinkableEntity = (entityId) => {
  const prefix = extractEntityPrefix(entityId);
  return prefix ? prefix in LINKABLE_ENTITIES : false;
};

/**
 * Check if an entity ID should be stripped (no modal, remove from display)
 * 
 * @param {string} entityId - The entity ID to check
 * @returns {boolean} True if the entity should be stripped
 */
export const isStrippableEntity = (entityId) => {
  const prefix = extractEntityPrefix(entityId);
  return prefix ? prefix in STRIP_ENTITIES : false;
};

/**
 * Get configuration for an entity by its ID
 * 
 * @param {string} entityId - The entity ID
 * @returns {Object|null} Entity config with type, color, label, linkable flag
 */
export const getEntityConfigById = (entityId) => {
  const prefix = extractEntityPrefix(entityId);
  if (!prefix) return null;
  
  if (prefix in LINKABLE_ENTITIES) {
    return { ...LINKABLE_ENTITIES[prefix], linkable: true, prefix };
  }
  if (prefix in STRIP_ENTITIES) {
    return { ...STRIP_ENTITIES[prefix], linkable: false, prefix };
  }
  return null;
};

/**
 * Check if text contains any entity IDs (linkable or strippable)
 * 
 * @param {string} text - The text to check
 * @returns {boolean} True if text contains entity IDs
 */
export const hasEntityIds = (text) => {
  if (!text || typeof text !== 'string') return false;
  
  const pattern = new RegExp(`(${ALL_PREFIXES.join('|')})_[a-f0-9_]+`, 'i');
  return pattern.test(text);
};

/**
 * Check if text contains any LINKABLE entity IDs
 * 
 * @param {string} text - The text to check
 * @returns {boolean} True if text contains linkable entity IDs
 */
export const hasLinkableEntityIds = (text) => {
  if (!text || typeof text !== 'string') return false;
  
  const linkablePrefixes = Object.keys(LINKABLE_ENTITIES);
  const pattern = new RegExp(`(${linkablePrefixes.join('|')})_[a-f0-9_]+`, 'i');
  return pattern.test(text);
};

// =============================================================================
// TEXT PROCESSING FUNCTIONS
// =============================================================================

/**
 * Strip all non-linkable entity IDs from text
 * 
 * Removes entity IDs that have no modal to link to (prod_, comp_, kpi_, etc.)
 * Handles both bare IDs and parenthesized formats.
 * Cleans up resulting double spaces.
 * 
 * NOTE: For KPIs, we only strip standalone kpi_xxx patterns.
 * Composite KPI keys (goal_xxx:kpi_xxx) are preserved because
 * they ARE linkable via the parent Goal modal.
 * 
 * @param {string} text - The text to process
 * @returns {string} Text with non-linkable IDs removed
 * 
 * @example
 * stripNonLinkableIds('Feature feat_123 (prod_456)') 
 * // returns 'Feature feat_123'
 */
export const stripNonLinkableIds = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  let result = text;
  
  // Strip each non-linkable entity type
  for (const prefix of Object.keys(STRIP_ENTITIES)) {
    // Special handling for KPI: use negative lookbehind to preserve composite keys
    // Composite keys have format goal_xxx:kpi_xxx - the colon indicates it's linkable
    if (prefix === 'kpi') {
      // Only strip kpi_ patterns NOT preceded by a colon (i.e., standalone KPIs)
      const kpiStripPattern = /(?<!:)[ \t]*\(?kpi_[a-z0-9_]+\)?[ \t]*/gi;
      result = result.replace(kpiStripPattern, ' ');
    } else {
      const stripPattern = createStripPattern(prefix);
      result = result.replace(stripPattern, ' ');
    }
  }
  
  // Clean up multiple horizontal spaces only (preserve newlines for markdown headers)
  result = result.replace(/[ \t]{2,}/g, ' ');
  
  // Trim edges if we created leading/trailing spaces
  // But preserve internal structure
  return result.trim() || result;
};

/**
 * Strip a specific entity ID from text (for context-aware suppression)
 * 
 * Used when viewing an entity to prevent self-referential links.
 * Handles both bare and parenthesized formats.
 * 
 * @param {string} text - The text to process
 * @param {string} entityId - The specific entity ID to suppress
 * @returns {string} Text with the specified ID removed
 * 
 * @example
 * suppressEntityId('Goal goal_123 relates to goal_456', 'goal_123')
 * // returns 'Goal relates to goal_456'
 */
export const suppressEntityId = (text, entityId) => {
  if (!text || typeof text !== 'string' || !entityId) return text;
  
  // Normalize the ID to suppress (handle variations)
  const normalizedId = normalizeEntityId(entityId);
  
  let result = text;
  
  // Remove with parentheses: (goal_xxx) -> empty
  result = result.replace(new RegExp(`\\(${normalizedId}\\)`, 'gi'), '');
  
  // Remove standalone: goal_xxx -> empty
  result = result.replace(new RegExp(normalizedId, 'gi'), '');
  
  // Also try to match the original if different from normalized
  if (normalizedId !== entityId) {
    result = result.replace(new RegExp(`\\(${entityId}\\)`, 'gi'), '');
    result = result.replace(new RegExp(entityId, 'gi'), '');
  }
  
  // Clean up double horizontal spaces only (preserve newlines for markdown)
  result = result.replace(/[ \t]{2,}/g, ' ');
  
  return result;
};

/**
 * Clean markdown formatting from around entity IDs
 * 
 * LLMs often wrap entity IDs in formatting like **feat_xxx** or `feat_xxx`.
 * This function strips the formatting so IDs can be properly processed.
 * 
 * @param {string} text - The text to process
 * @returns {string} Text with formatting stripped from entity IDs
 * 
 * @example
 * cleanEntityIdFormatting('Check **feat_123** and `epic_456`')
 * // returns 'Check feat_123 and epic_456'
 */
export const cleanEntityIdFormatting = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  // Reset regex state (global flag)
  FORMATTED_ENTITY_PATTERN.lastIndex = 0;
  
  return text.replace(FORMATTED_ENTITY_PATTERN, (match, openMark, entityId, prefix, closeMark) => {
    // Only strip formatting if BOTH opening AND closing markers are present
    // This prevents corrupting larger bold blocks like "**text entity_123**"
    // where only the closing ** would match the entity pattern
    if (openMark && closeMark) {
      return entityId;
    }
    // No matching pair - return as-is to preserve surrounding markdown
    return match;
  });
};

/**
 * Find all entity IDs in text with their positions and metadata
 * 
 * Returns detailed information about each entity ID found, including:
 * - Position in text (start, end)
 * - The entity ID itself
 * - Entity type and configuration
 * - Whether it's linkable
 * - Whether surrounding parentheses were found
 * - For composite KPI keys: goalId and kpiId components
 * 
 * @param {string} text - The text to scan
 * @param {Object} options - Processing options
 * @param {string} options.suppressEntityId - Entity ID to exclude from results
 * @returns {Array} Array of entity match objects
 */
export const findEntityIds = (text, options = {}) => {
  if (!text || typeof text !== 'string') return [];
  
  const { suppressEntityId: suppressId } = options;
  const normalizedSuppressId = suppressId ? normalizeEntityId(suppressId) : null;
  
  const matches = [];
  
  // ==========================================================================
  // STEP 1: Find composite KPI keys FIRST (goal_xxx:kpi_xxx)
  // These take precedence to avoid partial matches on the goal_xxx portion
  // ==========================================================================
  const compositeKpiPattern = /goal_[a-z0-9]+:(?:kpi_)?[a-z0-9_]+/gi;
  let compositeMatch;
  while ((compositeMatch = compositeKpiPattern.exec(text)) !== null) {
    const compositeKey = compositeMatch[0];
    
    // Skip if suppressed
    if (normalizedSuppressId && compositeKey.toLowerCase() === normalizedSuppressId.toLowerCase()) {
      continue;
    }
    
    const parsed = parseCompositeKpiKey(compositeKey);
    if (!parsed) continue;
    
    // Check if wrapped in parentheses
    const beforeChar = text[compositeMatch.index - 1];
    const afterChar = text[compositeMatch.index + compositeKey.length];
    const hasParens = beforeChar === '(' && afterChar === ')';
    
    matches.push({
      start: hasParens ? compositeMatch.index - 1 : compositeMatch.index,
      end: hasParens ? compositeMatch.index + compositeKey.length + 1 : compositeMatch.index + compositeKey.length,
      id: compositeKey,
      originalId: compositeKey,
      prefix: 'kpi_composite',
      type: 'kpi_composite',
      color: COMPOSITE_KPI_CONFIG.color,
      label: COMPOSITE_KPI_CONFIG.label,
      linkable: true,
      hasParens,
      // Composite KPI specific fields
      isCompositeKpi: true,
      goalId: parsed.goalId,
      kpiId: parsed.kpiId,
      targetType: COMPOSITE_KPI_CONFIG.targetType,
      targetTab: COMPOSITE_KPI_CONFIG.targetTab,
    });
  }
  
  // ==========================================================================
  // STEP 2: Find standard linkable entity IDs
  // ==========================================================================
  const linkablePrefixes = Object.keys(LINKABLE_ENTITIES);
  const linkablePattern = new RegExp(
    `(${linkablePrefixes.join('|')})_[a-f0-9_]+`,
    'gi'
  );
  
  let match;
  while ((match = linkablePattern.exec(text)) !== null) {
    const entityId = match[0];
    const normalizedId = normalizeEntityId(entityId);
    
    // Skip if this is the suppressed entity
    if (normalizedSuppressId && 
        (normalizedId.toLowerCase() === normalizedSuppressId.toLowerCase() ||
         entityId.toLowerCase() === normalizedSuppressId.toLowerCase())) {
      continue;
    }
    
    // Skip if this position is already covered by a composite KPI match
    // (e.g., goal_xxx in goal_xxx:kpi_xxx should not be double-matched)
    const alreadyCovered = matches.some(m => 
      match.index >= m.start && match.index < m.end
    );
    if (alreadyCovered) continue;
    
    const prefix = extractEntityPrefix(entityId);
    const config = LINKABLE_ENTITIES[prefix];
    
    // Check if wrapped in parentheses
    const beforeChar = text[match.index - 1];
    const afterChar = text[match.index + entityId.length];
    const hasParens = beforeChar === '(' && afterChar === ')';
    
    matches.push({
      // Position (include parens if present)
      start: hasParens ? match.index - 1 : match.index,
      end: hasParens ? match.index + entityId.length + 1 : match.index + entityId.length,
      // Entity info
      id: normalizedId,
      originalId: entityId,
      prefix,
      type: config.type,
      color: config.color,
      label: config.label,
      linkable: true,
      hasParens,
    });
  }
  
  // Sort by position and remove overlaps
  matches.sort((a, b) => a.start - b.start);
  
  const uniqueMatches = [];
  let lastEnd = -1;
  for (const m of matches) {
    if (m.start >= lastEnd) {
      uniqueMatches.push(m);
      lastEnd = m.end;
    }
  }
  
  return uniqueMatches;
};

// =============================================================================
// MAIN PROCESSING FUNCTION
// =============================================================================

/**
 * Process text for entity ID handling - THE UNIFIED ENTRY POINT
 * 
 * This is the main function that orchestrates all entity ID processing.
 * It handles stripping, suppression, formatting cleanup, and entity detection
 * in a single pass.
 * 
 * PROCESSING ORDER:
 * 1. Clean markdown formatting from entity IDs
 * 2. Strip non-linkable entity IDs (prod_, comp_, kpi_, etc.)
 * 3. Suppress specific entity ID if provided (context-aware)
 * 4. Find remaining linkable entities for rendering
 * 
 * @param {string} text - The text to process
 * @param {Object} options - Processing options
 * @param {string} options.suppressEntityId - Specific entity ID to suppress (e.g., current item)
 * @param {boolean} options.stripOnly - If true, just strip/suppress, don't find entities
 * @returns {Object} Processing result
 *   - text: Processed text with formatting cleaned and non-linkable IDs stripped
 *   - entities: Array of linkable entity matches (if stripOnly=false)
 *   - hasLinks: Boolean indicating if any linkable entities were found
 * 
 * @example
 * // Full processing for rendering
 * const result = processEntityIds('Check **feat_123** (prod_456)', { 
 *   suppressEntityId: 'epic_789' 
 * });
 * // result.text = 'Check feat_123'
 * // result.entities = [{ id: 'feat_123', type: 'feature', ... }]
 * // result.hasLinks = true
 * 
 * @example
 * // Strip-only mode (no entity detection)
 * const result = processEntityIds(text, { stripOnly: true });
 * // result.text = cleaned text
 * // result.entities = []
 * // result.hasLinks = false
 */
export const processEntityIds = (text, options = {}) => {
  if (!text || typeof text !== 'string') {
    return { text: text || '', entities: [], hasLinks: false };
  }
  
  const { suppressEntityId: suppressId, stripOnly = false } = options;
  
  // Step 1: Clean markdown formatting from entity IDs (removes ** around feat_123)
  let processedText = cleanEntityIdFormatting(text);
  
  // Step 2: Strip non-linkable entity IDs
  processedText = stripNonLinkableIds(processedText);
  
  // Step 3: Suppress specific entity ID if provided
  if (suppressId) {
    processedText = suppressEntityId(processedText, suppressId);
  }
  
  // Step 4: Find linkable entities (unless strip-only mode)
  // NOTE: Markdown spacing (bold/italic) is handled by rehypeSpacing plugin
  // in CopilotMarkdown.jsx AFTER markdown parsing, not here.
  if (stripOnly) {
    return { text: processedText, entities: [], hasLinks: false };
  }
  
  const entities = findEntityIds(processedText, { suppressEntityId: suppressId });
  
  return {
    text: processedText,
    entities,
    hasLinks: entities.length > 0,
  };
};

// =============================================================================
// LEGACY COMPATIBILITY EXPORTS
// =============================================================================

/**
 * @deprecated Use stripNonLinkableIds instead
 * Legacy function name for backward compatibility
 */
export const removeProductIds = stripNonLinkableIds;

/**
 * @deprecated Use cleanEntityIdFormatting instead
 * Legacy function name for backward compatibility
 */
export const cleanEntityIds = cleanEntityIdFormatting;

// =============================================================================
// EXPORTS SUMMARY
// =============================================================================

export default {
  // Configuration
  ENTITY_CONFIG,
  LINKABLE_ENTITIES,
  STRIP_ENTITIES,
  COMPOSITE_KPI_CONFIG,
  
  // Core functions
  normalizeEntityId,
  extractEntityPrefix,
  isLinkableEntity,
  isStrippableEntity,
  getEntityConfigById,
  
  // Composite KPI functions
  parseCompositeKpiKey,
  isCompositeKpiKey,
  
  // Detection functions
  hasEntityIds,
  hasLinkableEntityIds,
  findEntityIds,
  
  // Processing functions
  stripNonLinkableIds,
  suppressEntityId,
  cleanEntityIdFormatting,
  processEntityIds,
  
  // Legacy compatibility
  removeProductIds,
  cleanEntityIds,
};
